import { useMemo, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ControllerRenderProps,
  FieldErrors,
  useFormContext
} from 'react-hook-form'
import { BetriebsstelleField, Input } from '../../form'
import {
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField
} from '../Generic'
import { Betriebsstellenart } from '../../../types'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'
import SlideToUnlock from '../../SlideToUnlock'
import {
  DEFAULT_AUFTRAG_25_10,
  DEFAULT_AUFTRAG_25_20
} from './schema/befehl25-form.schema'

export interface Auftrag25_10FormProps {
  befehlKennung: string
  fieldPrefix: string
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
  displaySlideToUnlock: boolean
  errors?: FieldErrors
}

export default function Auftrag25_10Form({
  readOnly,
  fieldPrefix,
  'data-testid': testId,
  errors,
  displaySlideToUnlock,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag25_10FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch, resetField } = formContext

  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const { t } = useTranslation('ui-befehle')

  const { enabled } = watch(`${fieldPrefix}`)

  const selector = watch(`${fieldPrefix}.selector`)

  const context = useContext(MarkAuftraegeAsDoneContext)
  const befehlFrontendId = context?.befehlFrontendId
  const isQuittiert = context?.isBefehlsnachrichtQuittiert

  const [confirmed, setConfirmed] = useState<boolean>(
    context?.isDone(befehlFrontendId + '-25_10') || false
  )

  const updateSlideToUnlockState = (value: boolean) => {
    context?.setMarkedAsDone(befehlFrontendId + '-25_10', value)
    setConfirmed(value)
  }

  if (!readOnly || enabled) {
    return (
      <div className="relative">
        {confirmed && isQuittiert && (
          <div
            className="absolute z-30 inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
            data-testid={`${testId}-gray-out-overlay`}
          />
        )}

        <BefehlFormAuftrag
          auftragKennung="25.10"
          fieldName="auftrag_25_10.enabled"
          testId={testId}
          readOnly={readOnly}
          onChange={(checked) => {
            if (!checked) {
              resetField('auftrag_25_10', {
                defaultValue: DEFAULT_AUFTRAG_25_10
              })
              resetField('auftrag_25_20', {
                defaultValue: DEFAULT_AUFTRAG_25_20
              })
            }
          }}
        >
          <div className="flex flex-col flex-wrap items-baseline sm:flex-row w-full gap-0.5">
            <span data-testid={`${testId}-text`}>
              {t('auftraege.25_10.label')}
            </span>

            <BefehlFormField
              name={`${fieldPrefix}.km`}
              label={t('auftraege.25_10.km')}
              control={control}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-25-11`}
              render={({ field: kmField }) => (
                <div>
                  <Input
                    {...kmField}
                    label={t('auftraege.25_10.km')}
                    className="w-full"
                    disabled={!enabled}
                    readOnly={readOnly}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    data-testid={`${testId}-feld-25-11-inner`}
                    errors={fieldErrors}
                  />
                  <span
                    className={enabled ? '' : 'text-gray-500 opacity-60'}
                    data-testid={`${testId}-feld-25-11-label`}
                  >
                    {t('auftraege.25_10.km_label')}
                  </span>
                </div>
              )}
            />

            <span data-testid={`${testId}-text-auf-der`}>
              {t('auftraege.25_10.auf_der')}
            </span>

            <BefehlFormField
              name={`${fieldPrefix}.selector`}
              disabled={!enabled}
              control={control}
              label=""
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-25-12-13-selector`}
              render={({ field }: { field: ControllerRenderProps }) => (
                <AuftragOptionen
                  name={`${fieldPrefix}.selector`}
                  value={field.value}
                  disabled={!enabled}
                  onChange={field.onChange}
                  readOnly={readOnly}
                  errors={fieldErrors}
                  data-testid={`${testId}-feld-25-12-13-selector-options`}
                >
                  <AuftragOption
                    value={Betriebsstellenart.Abzw}
                    label={t('auftraege.25_10.abzw_label')}
                    data-testid={`${testId}-feld-25-12-13-selector-options-abzw`}
                    disabled={!enabled}
                  >
                    <BefehlFormField
                      name={`${fieldPrefix}.name`}
                      label={t('auftraege.25_10.abzw')}
                      control={control}
                      readOnly={readOnly}
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-25-12`}
                      render={({ field: zmField }) => (
                        <BetriebsstelleField
                          {...zmField}
                          label={t('auftraege.25_10.abzw')}
                          variant="solid"
                          className="w-full"
                          disabled={!enabled}
                          data-testid={`${testId}-feld-25-12-inner`}
                          errors={fieldErrors}
                          typ={selector}
                        />
                      )}
                    />
                  </AuftragOption>
                  <AuftragOption
                    value={Betriebsstellenart.Uest}
                    label={t('auftraege.25_10.uest_label')}
                    data-testid={`${testId}-feld-25-12-13-selector-options-uest`}
                    disabled={!enabled}
                  >
                    <BefehlFormField
                      name={`${fieldPrefix}.name`}
                      label={t('auftraege.25_10.uest')}
                      control={control}
                      readOnly={readOnly}
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-25-13`}
                      render={({ field: zmField }) => (
                        <BetriebsstelleField
                          {...zmField}
                          label={t('auftraege.25_10.uest')}
                          variant="solid"
                          className="w-full"
                          disabled={!enabled}
                          data-testid={`${testId}-feld-25-13-inner`}
                          errors={fieldErrors}
                          typ={selector}
                        />
                      )}
                    />
                  </AuftragOption>
                </AuftragOptionen>
              )}
            />
            <span data-testid={`${testId}-text-weiterfahren`}>
              {t('auftraege.25_10.weiterfahren')}
            </span>
          </div>
        </BefehlFormAuftrag>
        {context && displaySlideToUnlock && (
          <SlideToUnlock
            className="pt-0.75 sm:pl-5 sm:pr-5"
            data-testid={`${testId}-abgearbeitet-markieren-slideToUnlock`}
            confirmed={confirmed}
            onConfirmedChange={updateSlideToUnlockState}
            variant="secondary-solid"
          />
        )}
      </div>
    )
  }
}
